"use client";
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const item = (direction = 1) => ({
  hidden: {
    opacity: 0,
    y: 60,
    x: 40 * direction,
  },
  show: {
    opacity: 1,
    y: 0,
    x: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
});

export default function OurServices() {
  const { t } = useTranslation();

  // The numeric index stays the same, the content comes from your JSON
  const servicesList = [
    { id: "01", key: "s1" },
    { id: "02", key: "s2" },
    { id: "03", key: "s3" },
    { id: "04", key: "s4" },
    { id: "05", key: "s5" },
  ];

  return (
    <section id="services" className="relative bg-white py-40 overflow-hidden">
      <div className="absolute left-1/2 top-0 h-full w-px bg-gradient-to-b from-transparent via-slate-200 to-transparent" />

      <div className="relative max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="mb-40 text-center"
        >
          <h2 className="text-6xl md:text-7xl font-semibold tracking-tight text-slate-900">
            {t('services.main_title')}
          </h2>
          <p className="mt-8 text-xl text-slate-600 max-w-3xl mx-auto">
            {t('services.main_subtitle')}
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-120px" }}
          className="space-y-40"
        >
          {servicesList.map((service, i) => {
            const isLeft = i % 2 === 0;

            return (
              <div key={i} className="relative grid md:grid-cols-2 items-center gap-16">
                {/* Index Number */}
                <motion.div
                  variants={item(isLeft ? -1 : 1)}
                  className={`text-[8rem] md:text-[10rem] font-bold leading-none text-slate-100 select-none ${
                    isLeft ? "md:text-left" : "md:text-right md:order-2"
                  }`}
                >
                  {service.id}
                </motion.div>

                {/* Service Card */}
                <motion.div
                  variants={item(isLeft ? 1 : -1)}
                  className={`relative bg-white border border-slate-200 rounded-2xl p-10 md:p-12 shadow-[0_20px_60px_-25px_rgba(15,23,42,0.18)] ${
                    isLeft ? "md:-translate-x-12" : "md:translate-x-12 md:order-1"
                  }`}
                >
                  <h3 className="text-3xl font-semibold text-blue-900">
                    {t(`services.${service.key}_title`)}
                  </h3>
                  <div className="mt-4 h-px w-12 bg-slate-300" />
                  <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                    {t(`services.${service.key}_desc`)}
                  </p>
                </motion.div>

                {/* Spine dot */}
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-500"
                />
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}